import moment from 'moment';

const state = {
  parameters: null,
  invoices: [],
  expenses: [],
  payments: [],
  reversals: [],
  programs: [],
  paymentSummary: {},
  expenseSummary: {},
  invoiceSummary: {},
  reversalSummary: {},
};

const mutations = {
  Set_parameters(state, parameters) {
    state.parameters = parameters;
  },
  Set_invoices(state, invoices) {
    state.invoices = invoices;
  },
  Set_expenses(state, expenses) {
    state.expenses = expenses;
  },
  Set_payments(state, payments) {
    state.payments = payments;
  },
  Set_reversals(state, reversals) {
    state.reversals = reversals;
  },
  Set_programs(state, programs) {
    state.programs = programs;
  },
};

const actions = {
  setParameters({ commit }, parameters) {
    commit('Set_parameters', parameters);
  },
  setInvoices({ commit }, invoices) {
    commit('Set_invoices', invoices);
  },
  setExpenses({ commit }, expenses) {
    commit('Set_expenses', expenses);
  },
  setPayments({ commit }, payments) {
    commit('Set_payments', payments);
  },
  setReversals({ commit }, reversals) {
    commit('Set_reversals', reversals);
  },
  setAll(
    { commit, state },
    { invoices, payments, expenses, programs, reversals },
  ) {
    commit('Set_payments', payments);
    commit('Set_expenses', expenses);
    commit('Set_invoices', invoices);
    commit('Set_programs', programs);
    commit('Set_reversals', reversals);

    state.paymentSummary = {};
    state.expenseSummary = {};
    state.invoiceSummary = {};
    state.reversalSummary = {};
  },
  getInvoiceSummary({ state }, { program, dateRange }) {
    if (state.invoices == null || state.invoices.length === 0) {
      return null;
    }

    const id = program ? `${program}_${dateRange.id}` : dateRange.id;

    let summary = state.invoiceSummary[id];
    if (summary == null) {
      //filter invoices by date range
      const filteredInvoices = state.invoices.filter((i) => {
        const invoiceDate = moment(i.txDate).toDate();
        //if program is null, then only check date range; otherwise, match by program or "Others" program (which is when invoice program is null)
        return (
          (i.programName == program ||
            (program === 'Others' && i.programName == null) ||
            !program) &&
          invoiceDate >= dateRange.startTime &&
          invoiceDate <= dateRange.endTime
        );
      });

      summary = filteredInvoices.reduce((sum, invoice) => {
        if (sum) {
          if (invoice.paidAmount) {
            sum.paid += invoice.paidAmount;
          }

          if (invoice.unpaidAmount) {
            sum.unpaid += invoice.unpaidAmount;
          }
          sum.total += invoice.amount;
          sum.invoices.push(invoice);
        } else {
          sum = {
            program: program,
            date: dateRange.label,
            paid: invoice.paidAmount,
            unpaid: invoice.unpaidAmount,
            total: invoice.amount,
            invoices: [invoice],
          };
        }

        return sum;
      }, null);

      state.invoiceSummary[id] = summary;
    }

    return summary;
  },
  getPaymentSummary({ state }, { dateRange }) {
    if (state.payments == null || state.payments.length === 0) {
      return null;
    }

    const id = `${dateRange ? dateRange.id : '0'}`;
    let summary = state.paymentSummary[id];
    if (summary == null) {
      //filter payments by date range
      const filteredPayments = state.payments.filter((p) => {
        const paymentDate = moment(p.txDate).toDate();
        return (
          !dateRange ||
          (paymentDate >= dateRange.startTime &&
            paymentDate <= dateRange.endTime)
        );
      });
      summary = filteredPayments.reduce((sum, payment) => {
        if (sum) {
          sum.total += payment.amount;
          sum.payments.push(payment);
        } else {
          sum = {
            date: dateRange ? dateRange.label : 'All Date',
            total: payment.amount,
            payments: [payment],
          };
        }

        return sum;
      }, null);

      state.paymentSummary[id] = summary;
    }

    return summary;
  },
  getReversalSummary({ state }, { dateRange }) {
    if (state.reversals == null || state.reversals.length === 0) {
      return null;
    }

    const id = `${dateRange ? dateRange.id : '0'}`;
    let summary = state.reversalSummary[id];
    if (summary == null) {
      //filter reversals by date range
      const filteredReversals = state.reversals.filter((r) => {
        const reversalDate = moment(r.reversalDate).toDate();
        return (
          !dateRange ||
          (reversalDate >= dateRange.startTime &&
            reversalDate <= dateRange.endTime)
        );
      });

      console.log('filtered reversals: ', filteredReversals);

      summary = filteredReversals.reduce((sum, reversal) => {
        if (sum) {
          sum.total += reversal.amount;
          sum.reversals.push(reversal);
        } else {
          sum = {
            date: dateRange ? dateRange.label : 'All Date',
            total: reversal.amount,
            reversals: [reversal],
          };
        }

        return sum;
      }, null);

      state.reversalSummary[id] = summary;
    }

    return summary;
  },
  getExpenseSummary({ state }, { dateRange }) {
    if (state.expenses == null || state.expenses.length === 0) {
      return null;
    }

    const id = `${dateRange ? dateRange.id : '0'}`;
    let summary = state.expenseSummary[id];
    if (summary == null) {
      //filter expenses by date range
      const filteredExpenses = state.expenses.filter((e) => {
        const invoiceDate = moment(e.invoice_date).toDate();
        return (
          !dateRange ||
          (invoiceDate >= dateRange.startTime &&
            invoiceDate <= dateRange.endTime)
        );
      });
      summary = filteredExpenses.reduce((sum, expense) => {
        if (sum) {
          sum.total += expense.amount;
          sum.expenses.push(expense);
        } else {
          sum = {
            date: dateRange ? dateRange.label : 'All Date',
            total: expense.amount,
            expenses: [expense],
          };
        }

        return sum;
      }, null);

      state.expenseSummary[id] = summary;
    }

    return summary;
  },
};

const getters = {
  invoices: (state) => {
    return state.invoices;
  },
  expenses: (state) => {
    return state.expenses;
  },
  payments: (state) => {
    return state.payments;
  },
  reversals: (state) => {
    return state.reversals;
  },
  parameters: (state) => {
    return state.parameters;
  },
  programs: (state) => {
    return state.programs;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
